import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { valueRender } from '@repo/toolkit/conditional-render'
import { DateFormatter } from '@repo/toolkit/date'
import { useNavigator } from '@repo/toolkit/use-navigator'
import ActionButton from '@repo/ui/action-button'
import Badge from '@repo/ui/badge'
import { CheckMarkIcon, MeetingPointIcon, PenIcon } from '@repo/ui/icons'
import Input from '@repo/ui/input'
import Typography from '@repo/ui/typography'
import { useMutation } from '@tanstack/react-query'
import { Fragment, useState } from 'react'
import { toast } from 'sonner'

import { updateTripBooking } from '../../../../api/trip-bookings/update-trip-booking'
import { Paths } from '../../../../lib/constants/path'
import { TripStepTypeEnum } from '../../../../lib/types/enums/trip-bookings/trip-step-type.enum'
import { TripProperties } from '../../../../lib/types/models/trip/properties.trip'
import { TripBookingProperties } from '../../../../lib/types/models/trip-booking/properties.trip-booking'
import { TripFolderBooking } from '../../../../lib/types/models/trip-folder/trip-folder'
import { Time } from '../../../../lib/types/utils'
import { showAxiosError } from '../../../../lib/utils/functions/misc.functions'
import { TripFolderTimezone } from '../../../../views/trip-folder/trip-folder-details/folder-details'
import ReservationTime from './reservation-time'

type Props = {
  tripFolderBooking: TripFolderBooking
  activeTimezone: TripFolderTimezone
}

export const convertTimeZone = (
  time: Time | string,
  activeTimezone: TripFolderTimezone,
  timezone?: string,
  date?: Date | string
) => {
  if (!timezone) return DateFormatter.getFormattedTime(time)

  // If the timezone is local, we display the timezone coming from the trip step
  // Otherwise, we display the timezone coming from the active timezone which in our case is Paris (Europe/Paris)
  return DateFormatter.convertTimeZone(
    time,
    timezone,
    activeTimezone === 'Local' ? timezone : activeTimezone,
    date ? new Date(date) : undefined
  )
}

const TripFolderReservationDetails = ({ tripFolderBooking, activeTimezone }: Props) => {
  const [isEditing, setIsEditing] = useState(false)
  const [dockNumber, setDockNumber] = useState<string | undefined>(tripFolderBooking.dockNumber)

  const { navigate } = useNavigator()

  const { mutate, isPending } = useMutation({
    mutationFn: updateTripBooking,
    onError: showAxiosError,
    onSuccess() {
      toast.success('Quai/comptoir modifié avec succès')
    }
  })

  const handleEdit = () =>
    setIsEditing((prev) => {
      if (prev && dockNumber && dockNumber !== tripFolderBooking.dockNumber) {
        mutate({ id: tripFolderBooking.id, dockNumber: dockNumber })
      }

      return !prev
    })

  if (tripFolderBooking.isTakeInCharge) {
    return (
      <div
        className={'flex flex-col gap-2 w-full overflow-x-auto overflow-y-hidden mr-2 last:mr-0'}
      >
        <div className={'flex flex-col gap-2 w-full'}>
          <div className={'inline-flex flex-col gap-2 min-w-[500px]'}>
            <div className={'flex flex-row gap-2 items-center'}>
              <Badge
                icon={<MeetingPointIcon color={tailwindColors.red.DEFAULT} />}
                variant={'danger'}
                size={'xsmall'}
                className={'py-1 h-fit'}
              />
              <Typography variant={'secondary-body'} color={tailwindColors.red.DEFAULT}>
                {TripProperties.getTripLabel(tripFolderBooking.relatedTrip)}
              </Typography>
            </div>
            <div className={cn('flex gap-2')}>
              <Badge
                text={`Jeunes : ${tripFolderBooking.enrollmentsTotalCount} ${
                  tripFolderBooking.enrollments.length > 0
                    ? `(${tripFolderBooking.enrollments.map((enrollment) => `${enrollment.departurePlace.label}: ${enrollment.count}`).join(', ')})`
                    : ''
                }`}
                variant={'fushia'}
              />
              <Badge
                text={`Encadrants : ${tripFolderBooking.staffsTotalCount} ${
                  tripFolderBooking.staffs.length > 0
                    ? `(${tripFolderBooking.staffs.map((staff) => `${staff.departurePlace.label}: ${staff.count}`).join(', ')})`
                    : ''
                }`}
                variant={'blue'}
              />
            </div>

            <div className={'flex flex-row gap-1 items-center'}>
              <Typography variant={'label-semibold'} color={tailwindColors.primary.DEFAULT}>
                Prise en charge :
              </Typography>
              <Typography.Link
                variant={'label-semibold'}
                onClick={() =>
                  navigate(
                    Paths.TRIP_BOOKING_UPDATE_PAGE.replace(':id', tripFolderBooking.id.toString())
                  )
                }
              >
                {tripFolderBooking.label} :
              </Typography.Link>
              <ReservationTime
                label={'RDV Parent'}
                time={DateFormatter.getFormattedTime(tripFolderBooking.meetingTimeForParents)}
              />
            </div>
          </div>
        </div>
      </div>
    )
  }

  const orderedTripSteps =
    TripBookingProperties.sortTripStepsById(tripFolderBooking.tripSteps) ?? []

  return (
    <div className={'flex flex-col gap-2 w-full overflow-x-auto overflow-y-hidden mr-2 last:mr-0'}>
      <div className={'flex flex-col gap-2 w-full'}>
        <div className={'inline-flex flex-col gap-2 min-w-[500px]'}>
          {orderedTripSteps.length > 0 ? (
            <Fragment>
              <div className={cn('flex gap-2')}>
                <Badge
                  text={`Jeunes : ${tripFolderBooking.enrollmentsTotalCount} ${
                    tripFolderBooking.enrollments.length > 0
                      ? `(${tripFolderBooking.enrollments.map((enrollment) => `${enrollment.departurePlace.label}: ${enrollment.count}`).join(', ')})`
                      : ''
                  }`}
                  variant={'fushia'}
                />
                <Badge
                  text={`Encadrants : ${tripFolderBooking.staffsTotalCount} ${
                    tripFolderBooking.staffs.length > 0
                      ? `(${tripFolderBooking.staffs.map((staff) => `${staff.departurePlace.label}: ${staff.count}`).join(', ')})`
                      : ''
                  }`}
                  variant={'blue'}
                />
              </div>

              <div className="w-fit min-w-[500px] bg-white-dark rounded-[12px] p-1.5 flex flex-col gap-2">
                <Badge
                  variant={'white'}
                  color={tailwindColors.white.active}
                  className={'h-fit py-1 w-fit'}
                  text={
                    <div className={'flex items-center gap-2'}>
                      <Typography variant={'label-extrabold'}>
                        {tripFolderBooking.reservationLabel}
                      </Typography>
                      <div className={'flex items-center gap-1'}>
                        <Typography variant={'label-semibold'}>Départ</Typography>
                        <Badge
                          text={valueRender(orderedTripSteps.at(0)?.departureTime, (time) =>
                            convertTimeZone(
                              time,
                              activeTimezone,
                              orderedTripSteps.at(0)?.departureTimeZone
                            )
                          )}
                          variant="primary"
                          size="xsmall"
                        />
                      </div>
                      -
                      <div className="flex items-center gap-1">
                        <Typography variant={'label-semibold'}>Arrivée</Typography>
                        <Badge
                          text={valueRender(orderedTripSteps.at(-1)?.arrivalTime, (time) =>
                            convertTimeZone(
                              time,
                              activeTimezone,
                              orderedTripSteps.at(-1)?.arrivalTimeZone
                            )
                          )}
                          variant={'primary'}
                          size={'xsmall'}
                        />
                      </div>
                    </div>
                  }
                />

                {orderedTripSteps.map((step) => {
                  // OUTBOUND = "Retour": parents pick the group up after arrival, so the RDV
                  // is anchored to the arrival time/timezone. INBOUND = "Aller": before departure.
                  const isReturn = step.directionality === TripStepTypeEnum.OUTBOUND

                  const parentMeetingTime = isReturn
                    ? step.timeBeforeArrivalForParents
                    : step.timeBeforeDepartureForParents

                  const parentMeetingTimeZone = isReturn
                    ? step.arrivalTimeZone
                    : step.departureTimeZone

                  const rdvParentTime = parentMeetingTime ? (
                    <ReservationTime
                      label="RDV Parent"
                      time={convertTimeZone(
                        parentMeetingTime,
                        activeTimezone,
                        parentMeetingTimeZone
                      )}
                    />
                  ) : null

                  return (
                    <div key={step.id} className={'grid grid-cols-[1fr_2fr] w-full pl-2 gap-y-2'}>
                      {/* First row */}
                      <div className={'flex items-baseline gap-1 flex-1'}>
                        <Typography.Link
                          variant={'label-semibold'}
                          className="text-nowrap"
                          onClick={() =>
                            step.departurePlace?.id &&
                            navigate(
                              Paths.TRIP_ADDRESS_UPDATE_PAGE.replace(
                                ':id',
                                step.departurePlace.id.toString()
                              )
                            )
                          }
                        >
                          {step.departurePlace?.label}
                        </Typography.Link>
                        <span>-</span>
                        <Typography.Link
                          variant={'label-semibold'}
                          className="mr-1 text-nowrap"
                          onClick={() =>
                            step.arrivalPlace?.id &&
                            navigate(
                              Paths.TRIP_ADDRESS_UPDATE_PAGE.replace(
                                ':id',
                                step.arrivalPlace.id.toString()
                              )
                            )
                          }
                        >
                          {step.arrivalPlace?.label} :
                        </Typography.Link>
                      </div>
                      <div className="flex items-baseline gap-1 flex-1 flex-wrap">
                        {!isReturn && rdvParentTime && (
                          <Fragment>
                            {rdvParentTime}
                            <span>-</span>
                          </Fragment>
                        )}
                        <ReservationTime
                          label="Départ"
                          time={valueRender(step.departureTime, (time) =>
                            convertTimeZone(time, activeTimezone, step.departureTimeZone)
                          )}
                        />
                        <span>-</span>
                        <ReservationTime
                          label="Arrivée"
                          time={valueRender(step.arrivalTime, (time) =>
                            convertTimeZone(time, activeTimezone, step.arrivalTimeZone)
                          )}
                        />
                        {isReturn && rdvParentTime && (
                          <Fragment>
                            <span>-</span>
                            {rdvParentTime}
                          </Fragment>
                        )}
                      </div>

                      {/* Second row */}
                      {step.departureTimeTripProviders.length > 0 && (
                        <Fragment>
                          <Typography
                            variant={'label-semibold'}
                            color={tailwindColors.grey.lighter}
                          >
                            RDV prestataire :
                          </Typography>
                          <div className="flex gap-2">
                            {step.departureTimeTripProviders.map((provider) => (
                              <ReservationTime
                                key={provider.id}
                                label={`${provider.label}${provider.meetingPlace ? ` (RDV : ${provider.meetingPlace})` : ''}`}
                                time={convertTimeZone(
                                  provider.time,
                                  activeTimezone,
                                  step.departureTimeZone
                                )}
                              />
                            ))}
                          </div>
                        </Fragment>
                      )}
                    </div>
                  )
                })}
              </div>
            </Fragment>
          ) : (
            <div className="flex gap-1 items-center">
              <Typography variant={'label-semibold'} color={tailwindColors.primary.DEFAULT}>
                Pas de départ/arrivée pour la réservation [{tripFolderBooking.id}]
              </Typography>
            </div>
          )}

          <div className="flex gap-1 items-center">
            <Typography variant={'label-semibold'} color={tailwindColors.primary.DEFAULT}>
              Quai/comptoir :
            </Typography>
            <div className="flex gap-2 items-center">
              {/* Add controlled input later */}
              <Input
                type="text"
                placeholder="Quai/comptoir"
                className="w-32 h-8 min-h-8"
                wrapperClassName={cn('h-8 min-h-8', {
                  'pointer-events-none': !isEditing
                })}
                value={dockNumber}
                onChange={(e) => setDockNumber(e.target.value)}
              />
              <ActionButton
                variant="primary"
                icon={isEditing ? CheckMarkIcon : PenIcon}
                onClick={handleEdit}
                className="h-8"
                isLoading={isPending}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TripFolderReservationDetails
